import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Currency,
  formatDate,
  Pagination,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  SchemaFilterType,
  SortDir,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
} from '@ui/src';
import { useAppDispatch } from '@store';
import { Fintrac, FintracTransaction } from '../types';
import formatFintrac from '../helpers/format-fintrac';
import {
  useLazyGetFintracReportsQuery,
  useLazyGetFintracReportTransactionsQuery,
  useSelectTransactionInReportMutation,
  useDeselectAllTransactionInReportMutation,
  useSelectAllTransactionInReportMutation,
  useLazyGetFintracReportQuery,
  useDeleteFintracReportMutation,
  useCancelReportMutation,
  useSubmitReportMutation,
  useLazyDownloadReportQuery,
} from '../store/services';
import { setNotificationVisible } from '../../../../../../store/slice';
import {
  setSelectedTransactions,
  setSelectedReportId as setStoreSelectedReportId,
} from '../store/slices/list';
import { filtersSchema, schema } from '../variables/eftr';

const useFintracReport = ({
  reports,
  transactions,
  transactionsPagination,
  selectedTransactions,
  activePermissions,
  type,
  currencies,
}: {
  reports?: Fintrac[];
  transactions?: FintracTransaction[];
  transactionsPagination?: Pagination;
  selectedTransactions?: string[];
  activePermissions?: PermissionEnum[];
  type: 'eftr' | 'lvctr';
  currencies?: Currency[];
}) => {
  const [statusFilter, setStatusFilter] = useState('');
  const [cancelReportVisible, setCancelReportVisible] = useState(false);
  const [submitReportVisible, setSubmitReportVisible] = useState(false);
  const [addTransactionsSidepanelVisible, setAddTransactionsSidepanelVisible] =
    useState(false);
  const [selectedReportTransferDate, setSelectedReportTransferDate] =
    useState('');
  const [selectedReportId, setSelectedReportId] = useState('');
  const [selectedReportAmount, setSelectedReportAmount] = useState('');
  const [selectedTransactionId, setSelectedTransactionId] = useState('');
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [deleteReportId, setDeleteReportId] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const [getFintracReports, { isLoading, isFetching }] =
    useLazyGetFintracReportsQuery();
  const [
    getFintracReportTransactions,
    { isLoading: transactionsIsLoading, isFetching: transactionsIsFetching },
  ] = useLazyGetFintracReportTransactionsQuery();
  const [
    selectTransactionInReport,
    { isLoading: requestInProgress, isSuccess },
  ] = useSelectTransactionInReportMutation();
  const [deselectAllTransactions, { isLoading: deselectAllInProgress }] =
    useDeselectAllTransactionInReportMutation();
  const [
    selectAllTransactions,
    { isLoading: selectAllInProgress, isSuccess: selectAllIsSuccess },
  ] = useSelectAllTransactionInReportMutation();
  const [
    getFintracReport,
    { isLoading: reportIsLoading, isFetching: reportIsFetching },
  ] = useLazyGetFintracReportQuery();
  const [
    deleteFintracReport,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useDeleteFintracReportMutation();
  const [submitReport, { isLoading: submitInProgress }] =
    useSubmitReportMutation();
  const [cancelReport, { isLoading: cancelInProgress }] =
    useCancelReportMutation();
  const [
    downloadReport,
    { isLoading: documentIsLoading, isFetching: documentIsFetching },
  ] = useLazyDownloadReportQuery();

  const { search } = useLocation();
  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const cadCurrency = currencies?.find((item) => item.name === 'CAD');

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<Fintrac>({
      getList: getFintracReports,
      onFilterChange,
      onSortChange,
      schema,
    });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const onCheckStatus = async () => {
    await getFintracReport(selectedReportId);
  };

  const onSubmitReport = async () => {
    setDeleteError('');

    const response = (await submitReport(selectedReportId)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    } else {
      setSubmitReportVisible(false);

      dispatch(
        setNotificationVisible({
          visible: true,
          text: 'Report was submited',
          type: '',
          loading: false,
        }),
      );

      setTimeout(() => {
        getFintracReport(selectedReportId);
      }, 1500);

      setTimeout(() => {
        dispatch(
          setNotificationVisible({
            visible: false,
            text: '',
            type: '',
          }),
        );
      }, 3000);
    }
  };

  const onDownloadReport = async () => {
    const currentReport = reports?.find((item) => item.id === selectedReportId);

    if (currentReport) {
      const response = await downloadReport(currentReport?.uploadId as string);

      if (response.data) {
        // @ts-ignore
        const blob = new Blob([JSON.stringify(response.data)], {
          type: 'text/plain',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Fintrac Report ${formatDate(currentReport?.periodStart)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }
  };

  const onCancelReport = async () => {
    setDeleteError('');

    const response = (await cancelReport(selectedReportId)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    } else {
      setCancelReportVisible(false);

      dispatch(
        setNotificationVisible({
          visible: true,
          text: 'Report has been canceled',
          type: '',
          loading: false,
        }),
      );

      setTimeout(() => {
        getFintracReport(selectedReportId);
      }, 500);

      setTimeout(() => {
        dispatch(
          setNotificationVisible({
            visible: false,
            text: '',
            type: '',
          }),
        );
      }, 3000);
    }
  };

  const onDeleteClick = (id: string) => {
    setDeleteReportId(id);
    setDeletePopupVisible(true);
  };

  const onDeleteReport = async () => {
    setDeleteError('');

    const response = (await deleteFintracReport(deleteReportId)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    }
  };

  const onExpand = (id: string) => {
    getFintracReportTransactions(`?regulatoryReportId=${id}&take=15`);
    setSelectedReportId(id);
    dispatch(setStoreSelectedReportId(id));
  };

  const onToggleAll = async (toggle: boolean) => {
    if (!toggle) {
      await deselectAllTransactions(selectedReportId);
    } else {
      await selectAllTransactions(selectedReportId);
    }
  };

  const onRowsSelect = async (ids: string[], id?: string) => {
    dispatch(setSelectedTransactions(ids));

    if (id) {
      setSelectedTransactionId(id);

      await selectTransactionInReport({
        id,
        isSelectedForReport: !!ids?.includes(id),
      });
    }
  };

  const formattedData = useMemo(() => {
    const data = formatFintrac({
      list: reports as Fintrac[],
      fintracTransactions: transactions,
      transactionsPagination,
      onRowsSelect,
      selectedTransactions,
      setCancelReportVisible,
      setSubmitReportVisible,
      setSelectedReportAmount,
      setSelectedReportTransferDate,
      setAddTransactionsSidepanelVisible,
      type,
      transactionsIsLoading: transactionsIsLoading || transactionsIsFetching,
      onToggleAll,
      deselectAllInProgress,
      selectAllInProgress,
      requestInProgress,
      selectedTransactionId,
      cadCurrency,
      currencies,
      onDeleteClick,
      activePermissions,
      onCheckStatus,
      reportIsLoading: reportIsLoading || reportIsFetching,
      onDownloadReport,
      reportDownloadInProgress: documentIsLoading || documentIsFetching,
    });

    return data;
  }, [
    reports,
    transactions,
    transactionsPagination,
    selectedTransactions,
    transactionsIsLoading,
    transactionsIsFetching,
    onToggleAll,
    deselectAllInProgress,
    selectAllInProgress,
    requestInProgress,
    selectedTransactionId,
    cadCurrency,
    currencies,
    activePermissions,
    documentIsLoading,
    documentIsFetching,
  ]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedTransactionId('');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (selectAllIsSuccess && selectedReportId) {
      setTimeout(() => {
        getFintracReport(selectedReportId);
      }, 5);
    }
  }, [selectAllIsSuccess, selectedReportId]);

  useEffect(() => {
    if (deleteIsSuccess) {
      setDeletePopupVisible(false);
      setDeleteError('');
      setDeleteReportId('');

      const filtersValues: any = {
        ...filters,
        take: {
          value: ['30'],
          type: SchemaFilterType.STRING,
        },
        reportType: {
          value: [type === 'eftr' ? 'EFTR' : 'LVCTR'],
          type: SchemaFilterType.STRING,
        },
        sortProp: {
          value: ['createdAt'],
          type: SchemaFilterType.STRING,
        },
        sortDir: {
          value: [SortDir.DESC],
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: filtersValues,
      });
    }
  }, [deleteIsSuccess, filters]);

  useEffect(() => {
    if (
      !reports &&
      filters &&
      prevFilters &&
      activePermissions?.includes(PermissionEnum.REGULATORY_REPORT_FINTRAC_LIST)
    ) {
      const filtersValues: any = {
        ...filters,
        take: {
          value: ['30'],
          type: SchemaFilterType.STRING,
        },
        reportType: {
          value: [type === 'eftr' ? 'EFTR' : 'LVCTR'],
          type: SchemaFilterType.STRING,
        },
        sortProp: {
          value: ['createdAt'],
          type: SchemaFilterType.STRING,
        },
        sortDir: {
          value: [SortDir.DESC],
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: filtersValues,
      });
    }
  }, [filters, search, reports, prevFilters, activePermissions, type]);

  return {
    search,
    statusFilter,
    setStatusFilter,
    formattedData,
    onExpand,
    onRowsSelect,
    cancelReportVisible,
    setCancelReportVisible,
    submitReportVisible,
    setSubmitReportVisible,
    selectedReportAmount,
    addTransactionsSidepanelVisible,
    setAddTransactionsSidepanelVisible,
    selectedReportTransferDate,
    setSelectedReportTransferDate,
    isLoading,
    isFetching,
    setPage,
    onSort,
    errorList,
    sort,
    filtersComponent,
    handleSubmit,
    onFilter,
    filters,
    selectedReportId,
    deletePopupVisible,
    setDeletePopupVisible,
    setDeleteReportId,
    deleteError,
    deleteInProgress,
    onDeleteReport,
    onSubmitReport,
    submitInProgress,
    onCancelReport,
    cancelInProgress,
    setDeleteError,
  };
};

export default useFintracReport;
